import React, { Fragment } from 'react'
import styled from 'styled-components'

import LiveTooltip from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'

import { PackageFeatureResource, PackageResource } from 'plugin-package-manager/webapp/src/http/api'
import { FeatureTypes } from '../types'
import { getFeatureValidationStatus, getPackageValidationStatus, getRequiredEntitiesValidationStatus } from './utils'

export const statusColors: Record<string, string> = {
    VALID: 'green-700',
    INVALID: 'red-700',
    VALID_BUT: 'yellow-700',
    INACTIVE: 'gray-600',
    NEW: 'blue-700'
}

interface TagProps {
    asButton?: boolean
    hideLabel?: boolean
    iconClassName?: string
    color: string
    children: React.ReactNode
    title?: string | JSX.Element
}

function Tag({ asButton, hideLabel, iconClassName, color, children, title }: TagProps) {
    return (
        <LiveTooltip content={title} position="right" hide={!title}>
            <Wrapper as={asButton ? 'button' : undefined} color={color}>
                <>
                    {iconClassName && <i aria-hidden="true" className={iconClassName} />}
                    <span className={hideLabel ? 'sr-only' : ''}>{children}</span>
                    {asButton && <i aria-hidden="true" className="fa fa-caret-down" />}
                </>
            </Wrapper>
        </LiveTooltip>
    )
}

export const FeatureStatusTag: React.VFC<{
    pkgOriginallyEnabled: boolean
    feature: PackageFeatureResource
    isFirstConfig?: boolean
}> = ({ pkgOriginallyEnabled, isFirstConfig, feature }) => {
    let status = getFeatureValidationStatus(pkgOriginallyEnabled, feature)

    const messages = [...feature.status?.messages]

    const isInstalled = feature.status?.flags?.includes('INSTALLED')

    if (isFirstConfig && feature.type === FeatureTypes.DASHBOARD) {
        status = 'NEW'
        messages.push(i18n('This feature is not currently installed'))
    }

    if (status === 'INACTIVE') {
        messages.push(i18n('Feature not installed because the package is INACTIVE'))
    } else {
        if (feature.enabled && !isInstalled) {
            status = 'INVALID'
            messages.push(i18n('Enabled but not installed'))
        }

        if (feature.installed?.entityState?.status == 'INVALID') {
            status = 'INVALID'
            messages.push(i18n('Installed object with invalid status'))
            messages.push(...feature.installed.entityState.messages)
        }

        if (feature.installed?.entityState?.status == 'VALID_BUT') {
            status = 'VALID_BUT'
            messages.push(...feature.installed.entityState.messages)
            messages.push('\n')
        }

        if (!feature.enabled && status === 'VALID') {
            // no need to display valid if not enabled
            return null
        }
    }

    if (isInstalled) {
        messages.push(i18n('Feature installed'))
    }

    return (
        <Tag color={statusColors[status]} title={getMessagesAsJSX(messages)}>
            {i18n(status)}
        </Tag>
    )
}

export const RequiredEntitiesStatusTag = ({ requiredEntity }) => {
    const status = getRequiredEntitiesValidationStatus(requiredEntity)

    const messages = [...requiredEntity.state?.messages]

    return (
        <>
            <Tag color={statusColors[status]} title={getMessagesAsJSX(messages)}>
                {i18n(status)}
            </Tag>

            {messages.includes('Mnemonic mapping is missing') && (
                <div style={{ marginTop: '4px' }}>
                    Go to the{' '}
                    <a target="_blank" href="/#/config/standard-channels">
                        Standard channels
                    </a>
                </div>
            )}
        </>
    )
}

const getMessagesAsJSX = (messages: string[]): JSX.Element | string => {
    if (_.isEmpty(messages)) {
        return ''
    }

    return (
        <span>
            {messages.map((message, index) => (
                <Fragment key={index}>
                    {message}
                    <br />
                </Fragment>
            ))}
        </span>
    )
}

export const PackageStatusTag = ({ package: pkg }: { package: PackageResource }) => {
    let status = getPackageValidationStatus(pkg)
    const messages = [...pkg.status.messages, ...pkg.pluginState?.messages]

    // roll up features status at package level
    pkg.features?.forEach(feature => {
        if (feature.status?.flags?.includes('INVALID')) {
            status = 'INVALID'
            messages.push(...feature.status.messages)
        }
    })

    pkg.requiredEntities?.forEach(entity => {
        if (entity.state?.status.includes('INVALID')) {
            status = 'INVALID'
            messages.push(
                `Required entity ${entity.type} ${entity.key} with invalid state: ` + entity.state.messages.join(', ')
            )
        }
    })

    return (
        <Tag title={getMessagesAsJSX(messages)} color={statusColors[status]}>
            {i18n(status)}
        </Tag>
    )
}

export default Tag

const Wrapper = styled.div<{ color: string }>`
    display: inline-flex;
    align-items: center;
    margin: 0;
    padding: 2px 8px 2px 0;
    border: 1px solid;
    border-radius: 3px;
    background: transparent;
    color: var(--${({ color }) => color});
    height: 20px;
    font-size: 12px;
    line-height: 1;
    text-transform: uppercase;

    > * {
        margin-left: 8px;
    }

    span {
        font-weight: 600;
    }

    i {
        font-size: 11px;
        pointer-events: none;
    }
`
